/**
 * ReturnToParentButton — the shared "Return to session" pill for a child
 * specialist session (compatibility, … any future sub-app). Renders nothing on a
 * standalone (non-child) session.
 *
 * The handoff logic lives in useReturnToParent (one place); this is just the
 * standard visual control so each sub-app stops re-styling and re-wiring its own
 * return button. Apps with a bespoke control (alg's header chevron + FHIR-summary
 * CTA) call useReturnToParent directly instead of this component.
 */

import { useTranslation } from 'react-i18next'
import { CornerUpLeft } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useReturnToParent } from '@/hooks/useSessionHandoff'

interface ReturnToParentButtonProps {
  /** The parent session to return to. Null/undefined → renders nothing. */
  parentSessionId: string | null | undefined
  /** This child session's id (appended as ?from_child for the parent's pull). */
  childSessionId: string
  /** Override the default "Return to session" label. */
  label?: string
  className?: string
}

export function ReturnToParentButton({
  parentSessionId,
  childSessionId,
  label,
  className,
}: ReturnToParentButtonProps) {
  const { t } = useTranslation()
  const { isChild, returnToParent } = useReturnToParent(parentSessionId, childSessionId)
  if (!isChild) return null
  return (
    <Button
      type="button"
      size="pill"
      onClick={returnToParent}
      className={className ?? 'min-h-[44px] gap-1.5'}
      data-testid="return-to-parent"
    >
      <CornerUpLeft className="h-4 w-4" aria-hidden="true" />
      {label ?? t('session.returnToParent.label', 'Return to session')}
    </Button>
  )
}
